import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { ArrowLeft, Plus, Trash2, Edit3, BookOpen, FileText, AlertCircle, CheckCircle } from 'lucide-react'
import { buttonVariants, componentClasses, cn } from '../../utils/designSystem'
import { calculateTotalWords, validateOutline } from './skripsiUtils'

const SkripsiOutlinePage = ({ query, outline, onOutlineChange, onStartGeneration, onBack, llmService }) => {
  const [editingSection, setEditingSection] = useState(null)
  const [newSectionTitle, setNewSectionTitle] = useState('')
  const [showAddSection, setShowAddSection] = useState(false)

  // Remove word count validation from UI (keep for backend only)

  const handleEditSection = (sectionId, field, value) => {
    const updatedOutline = outline.map(section => {
      if (section.id === sectionId) {
        return { ...section, [field]: value }
      }
      return section
    })
    onOutlineChange(updatedOutline)
  }

  const handleDeleteSection = (sectionId) => {
    const updatedOutline = outline.filter(section => section.id !== sectionId)
    onOutlineChange(updatedOutline)
  }

  const handleAddSection = () => {
    if (!newSectionTitle.trim()) return
    
    const newSection = {
      id: `custom_${Date.now()}`,
      title: newSectionTitle,
      minWords: 1000, // Default minimal words for backend
      description: '',
      isEditable: true,
      topic: query
    }
    
    onOutlineChange([...outline, newSection])
    setNewSectionTitle('')
    setShowAddSection(false)
  }

  const handleStartGeneration = () => {
    onStartGeneration(outline)
  }

  return (
    <div className="min-h-screen bg-bg-primary">
      {/* Header */}
      <div className="bg-bg-secondary border-b border-border-primary sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="flex items-center gap-2 px-3 py-2 text-text-tertiary hover:text-text-primary transition-colors rounded-lg hover:bg-bg-tertiary"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm font-medium">Kembali</span>
              </button>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-emerald-500/10 rounded-lg">
                  <BookOpen className="w-6 h-6 text-emerald-500" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-text-primary">Skripsi Agent</h1>
                  <p className="text-sm text-text-tertiary">Konfigurasi Outline Skripsi</p>
                </div>
              </div>
            </div>
            
            <button
              onClick={handleStartGeneration}
              className={cn(buttonVariants.primary, "flex items-center")}
            >
              <FileText className="w-4 h-4 mr-2" />
              Buat Skripsi
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Topic Display */}
        <div className="bg-bg-secondary rounded-xl p-6 mb-8 border border-border-primary">
          <h2 className="text-lg font-semibold text-text-primary mb-2">Topik Skripsi</h2>
          <p className="text-text-secondary text-lg">"{query}"</p>
        </div>

        {/* Validation errors removed from UI */}

        {/* Outline Sections */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-text-primary">Outline Skripsi</h2>
            <button
              onClick={() => setShowAddSection(true)}
              className={cn(buttonVariants.secondary, "text-sm flex items-center")}
            >
              <Plus className="w-4 h-4 mr-2" />
              Tambah Bagian
            </button>
          </div>

          {outline.map((section, index) => (
            <div key={section.id} className="bg-bg-secondary rounded-xl border border-border-primary overflow-hidden">
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="text-sm font-medium text-text-tertiary bg-bg-tertiary px-2 py-1 rounded">
                        #{index + 1}
                      </span>
                      {editingSection === section.id ? (
                        <input
                          type="text"
                          value={section.title}
                          onChange={(e) => handleEditSection(section.id, 'title', e.target.value)}
                          onBlur={() => setEditingSection(null)}
                          onKeyDown={(e) => e.key === 'Enter' && setEditingSection(null)}
                          className="flex-1 bg-bg-primary border border-border-primary rounded-lg px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                          autoFocus
                        />
                      ) : (
                        <h3 className="text-lg font-semibold text-text-primary">{section.title}</h3>
                      )}
                    </div>
                    
                    {section.description && (
                      <p className="text-text-secondary mb-3">{section.description}</p>
                    )}
                    
                    {section.subsections && section.subsections.length > 0 && (
                      <div className="mb-3">
                        <p className="text-sm font-medium text-text-tertiary mb-2">Sub-bagian:</p>
                        <div className="flex flex-wrap gap-2">
                          {section.subsections.map((sub, subIndex) => (
                            <span key={subIndex} className="text-xs bg-bg-tertiary text-text-secondary px-2 py-1 rounded">
                              {typeof sub === 'string' ? sub : sub.title || sub.description || 'Sub-bagian'}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Word count removed from UI but kept in backend */}
                  </div>
                  
                  {section.isEditable && (
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => setEditingSection(editingSection === section.id ? null : section.id)}
                        className="p-2 text-text-tertiary hover:text-text-primary transition-colors rounded-lg hover:bg-bg-tertiary"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteSection(section.id)}
                        className="p-2 text-red-400 hover:text-red-300 transition-colors rounded-lg hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Add New Section Form */}
          {showAddSection && (
            <div className="bg-bg-secondary rounded-xl border border-border-primary p-6">
              <h3 className="text-lg font-semibold text-text-primary mb-4">Tambah Bagian Baru</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-tertiary mb-2">Judul Bagian</label>
                  <input
                    type="text"
                    value={newSectionTitle}
                    onChange={(e) => setNewSectionTitle(e.target.value)}
                    placeholder="Masukkan judul bagian..."
                    className="w-full bg-bg-primary border border-border-primary rounded-lg px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {/* Minimal kata input removed from UI */}
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleAddSection}
                    disabled={!newSectionTitle.trim()}
                    className={cn(
                      buttonVariants.primary,
                      "text-sm flex items-center",
                      !newSectionTitle.trim() && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Tambah
                  </button>
                  <button
                    onClick={() => setShowAddSection(false)}
                    className={cn(buttonVariants.secondary, "text-sm flex items-center")}
                  >
                    Batal
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="mt-8 bg-bg-secondary rounded-xl p-6 border border-border-primary">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-2">Ringkasan</h3>
              <div className="space-y-1">
                <p className="text-text-secondary">
                  <span className="font-medium">{outline.length}</span> bagian total
                </p>
              </div>
            </div>
            
            <button
              onClick={handleStartGeneration}
              className={cn(
                buttonVariants.primary,
                "text-lg px-8 py-3 flex items-center"
              )}
            >
              <FileText className="w-5 h-5 mr-3" />
              Buat Skripsi
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

SkripsiOutlinePage.propTypes = {
  query: PropTypes.string.isRequired,
  outline: PropTypes.array.isRequired,
  onOutlineChange: PropTypes.func.isRequired,
  onStartGeneration: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  llmService: PropTypes.object.isRequired
}

export default SkripsiOutlinePage